import { Box, IconButton, useMediaQuery, useTheme } from '@chakra-ui/react';
import { FC, Fragment, ReactNode, useState } from 'react';
import FocusLock from 'react-focus-lock';
import { RemoveScroll } from 'react-remove-scroll';
import { useKey } from 'react-use';

import ExpandIcon from '@/assets/icons/ic-expand.svg';
import MenuIcon from '@/assets/icons/ic-menu.svg';

import { Overlay } from '@/components/ui/core/Overlay/Overlay';

import { NavigationDrawer } from '../NavigationDrawer/NavigationDrawer';
import { useNavigationRailStore } from './NavigationRail.store';

// Pure drawer mechanics: the hamburger trigger, dimming overlay, background-scroll lock and
// focus trap. Knows nothing about what's being navigated to - that's `children`, supplied by
// NavigationRailContent. On desktop it stays permanently open as a static rail; below `lg` it
// becomes a slide-out drawer. The same hamburger opens the drawer below `lg` and expands the rail
// on desktop - two behaviours because there is nothing to slide out when the rail is always there.
export const NavigationRail: FC<{ children: ReactNode }> = ({ children }) => {
  const { breakpoints } = useTheme();
  const [isMobileViewport] = useMediaQuery(`(max-width: ${breakpoints.lg})`);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isDrawerVisible = isMobileViewport && isDrawerOpen;
  const isExpanded = useNavigationRailStore((state) => state.isExpanded);
  const shouldAnimate = useNavigationRailStore((state) => state.shouldAnimate);
  const toggle = useNavigationRailStore((state) => state.toggle);

  useKey('Escape', () => setIsDrawerOpen(false));

  // TODO: should close the drawer on route switch - not important atm since all the pages
  // render their own layout

  return (
    <Fragment>
      <Overlay zIndex={1000} hidden={!isDrawerVisible} onClick={() => setIsDrawerOpen(false)} />

      <IconButton
        aria-label="Open navigation menu"
        variant="unstyled"
        display={{ base: 'flex', lg: 'none' }}
        px={{ base: '19px', md: '35px' }}
        py={6}
        ml={{ base: '-19px', md: '-35px' }}
        icon={<MenuIcon height="1.5rem" width="1.5rem" />}
        onClick={() => setIsDrawerOpen(true)}
      />

      <RemoveScroll enabled={isDrawerVisible} forwardProps>
        <Box className="scroll" zIndex={1000}>
          <FocusLock as={NavigationDrawer} disabled={!isDrawerVisible} lockProps={{ 'aria-expanded': isDrawerOpen }}>
            {/* First row of the rail itself, in the icons' own column - the drawer has the hamburger in
                the header instead, so there is nothing to toggle below `lg`. */}
            <IconButton
              aria-label={isExpanded ? 'Collapse navigation menu' : 'Expand navigation menu'}
              aria-expanded={isExpanded}
              variant="unstyled"
              display={{ base: 'none', lg: 'flex' }}
              justifyContent="center"
              alignItems="center"
              boxSize="3.5rem"
              minW="3.5rem"
              flexShrink={0}
              mb={2}
              // the icon's chevron points the way the rail will move, so it turns around once expanded
              icon={
                <ExpandIcon
                  height="1.5rem"
                  width="1.5rem"
                  style={{
                    transform: isExpanded ? 'rotate(180deg)' : undefined,
                    transition: shouldAnimate ? 'transform .2s ease' : undefined,
                  }}
                />
              }
              onClick={toggle}
            />
            {children}
          </FocusLock>
        </Box>
      </RemoveScroll>
    </Fragment>
  );
};
